import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "@components/Loader";
import { useShopShippingQuery, useSaveShopShippingMutation } from "@queries/website";
import "@styles/opencart-admin.css";

// Free-shipping rule for SHOP orders only (washywash.com/shop/..., e.g.
// product 4018) — separate from the delivery fee used by Wash & Fold,
// carpets, express, etc. Read live by both the website and the Android app
// (same /api/v2 backend), so saving here updates both immediately.
export default function ShopShippingPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useShopShippingQuery();
  const saveMutation = useSaveShopShippingMutation();

  const [threshold, setThreshold] = React.useState("");
  const [fee, setFee] = React.useState("");
  const [savedAt, setSavedAt] = React.useState(null);

  React.useEffect(() => {
    if (data) {
      setThreshold(String(data.free_shipping_threshold));
      setFee(String(data.delivery_fee));
    }
  }, [data]);

  async function handleSave(e) {
    e.preventDefault();
    await saveMutation.mutateAsync({
      free_shipping_threshold: Number(threshold),
      delivery_fee: Number(fee)
    });
    queryClient.invalidateQueries({ queryKey: ["website", "shop-shipping"] });
    setSavedAt(Date.now());
  }

  if (isLoading) return <Loader />;

  return (
    <div className="oc-page">
      <h1>Shop Shipping</h1>
      <p style={{ color: "#666", maxWidth: 620 }}>
        Controls the delivery fee for <strong>Shop</strong> orders only
        (washywash.com/shop/...) — e.g. detergent, softener. This does not
        affect Wash &amp; Fold, carpets, furniture, car detailing or express
        orders, which have their own delivery rules. Applies to both the
        website and the mobile app immediately — no deploy needed.
      </p>

      <form onSubmit={handleSave} className="card" style={{ maxWidth: 480, padding: 20 }}>
        <div className="form-group">
          <label>Free shipping when the shop order total is at or above (JOD)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            className="input"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            required
          />
        </div>

        <div className="form-group" style={{ marginTop: 16 }}>
          <label>Delivery fee below that amount (JOD)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            className="input"
            value={fee}
            onChange={(e) => setFee(e.target.value)}
            required
          />
        </div>

        <p style={{ fontSize: 13, color: "#777", marginTop: 14 }}>
          Example: with {threshold || "…"} / {fee || "…"} — an order of{" "}
          {Number(threshold || 0) - 0.01 >= 0 ? (Number(threshold || 0) - 0.01).toFixed(2) : "0.00"} JOD
          costs {fee || "…"} JOD delivery, and an order of {threshold || "…"} JOD or more ships free.
        </p>

        <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 12 }}>
          <button type="submit" className="btn btn-primary" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? "Saving..." : "Save"}
          </button>
          {savedAt && !saveMutation.isPending && (
            <span style={{ color: "#2e7d32", fontSize: 13 }}>Saved ✓</span>
          )}
          {saveMutation.isError && (
            <span style={{ color: "#b3261e", fontSize: 13 }}>
              {saveMutation.error?.response?.data?.message || "Failed to save."}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
